import { useState } from 'react';
import { useQuery } from 'react-query';

import { Avatar, Box, Card, Flex, Heading, Skeleton, Stack, Text } from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { ClampedTextV2 } from '~/components/clamped-text-v2';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { REVIEW_MINIMUM_THRESHOLD } from '~/features/program/constants';
import { getProgramReviews } from '~/features/program/controllers/get-program-reviews';
import { ProgramReview, ProgramReviews } from '~/features/program/models/program-review';
import { ProgramReviewSummary } from '~/features/program/models/program-review-summary';
import { useClientRender } from '~/hooks/use-client-render';
import { format, useFormatDate } from '~/hooks/use-format-date';

import { useExperimentalSegmentEvents } from '../hooks';
import { ICON_SIZE, StarIcon } from '../icons';

import { Pagination } from './pagination';
import { Rating } from './rating';

const REVIEW_PAGE_SIZE = 5 as const;

export type ReviewsSectionProps = NonNullable<StaticReviewsSectionProps>;

export function ReviewsSection({ programKey, initReviews, numberOfReviews, starsAverage }: ReviewsSectionProps) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(numberOfReviews / REVIEW_PAGE_SIZE);
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const isClientRender = useClientRender();

  const { data: reviews, isLoading: reviewsIsLoading } = useQuery<ProgramReviews>(
    ['reviews', page],
    () => getProgramReviews(programKey, page),
    {
      initialData: page === 1 && initReviews ? initReviews : undefined,
      enabled: isClientRender,
    }
  );

  function handlePageChange(page: number) {
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'Product Page CTA Clicked',
        type: 'click',
        label: 'reviews_scroll_clicked',
        additionalAttributes: {
          product_ids: [programKey],
        },
      });
    }
    setPage(page);
  }

  return (
    <Box as='section' id='reviews' scrollMarginTop={`calc(${headerHeight} + 24px)`}>
      <ContentContainer>
        <Flex alignItems='center' flexWrap='wrap' gap='8px' justifyContent='space-between' mb={4}>
          <Heading
            as='h2'
            size={{
              base: 'heading-6',
              md: 'heading-5',
            }}
          >
            Reviews
          </Heading>
          <Flex alignItems='center' gap='6px'>
            <StarIcon color='primary.orange' height={ICON_SIZE} width={ICON_SIZE} />
            <Text as='b' color='supportOrange.70' fontSize='md' fontWeight='600' lineHeight='md'>
              Average Rating: {starsAverage}{' '}
              <Text as='span' color='supportGray.100' fontSize='md' fontWeight='400' lineHeight='md'>
                ({numberOfReviews} Reviews)
              </Text>
            </Text>
          </Flex>
        </Flex>
        <Flex flexDirection='column' gap='16px' mb={8}>
          {!reviewsIsLoading && (
            <>
              {reviews?.map((review, idx) => {
                const avatarColor = ['supportGray.20', 'secondary.purple', 'supportOrange.10'][idx % 3];
                return <ReviewCard key={review.createdAt + idx} avatarColor={avatarColor} review={review} />;
              })}
            </>
          )}
          {reviewsIsLoading && (
            <>
              <ReviewSkeletonCard />
              <ReviewSkeletonCard />
              <ReviewSkeletonCard />
              <ReviewSkeletonCard />
              <ReviewSkeletonCard />
            </>
          )}
        </Flex>
        <Flex alignItems='center' justifyContent='space-between'>
          <Pagination currentPage={page} totalPages={totalPages} onChange={handlePageChange} />
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function ReviewCard({ review, avatarColor = 'supportGray.20' }: { review: ProgramReview; avatarColor?: string }) {
  const formattedDate = useFormatDate({ date: review.createdAt, format: format.monthDayYearShort, forceUTC: true });
  return (
    <Card
      as='article'
      border='1px solid'
      borderColor='supportBlue.20'
      borderRadius='8px'
      boxShadow='none'
      display='flex'
      overflow='hidden'
      flexDirection={{
        base: 'column',
        md: 'row',
      }}
    >
      <Flex
        flex='1'
        flexWrap='wrap'
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: 2,
          md: 4,
        }}
        padding={{
          base: 2,
          md: 6,
        }}
      >
        <Box
          flex={{
            md: '0 0 80px',
          }}
        >
          <Rating rating={review.stars} />
        </Box>
        <Box
          as='blockquote'
          flex={{
            md: '1 1 400px',
          }}
          minHeight={{
            md: '46.5px',
          }}
        >
          <ClampedTextV2
            noOfLines={1}
            renderButton={({ isClamped, onToggle }) => {
              return (
                <Text
                  as='button'
                  color='primary.blue'
                  fontSize='sm'
                  fontWeight='700'
                  lineHeight='sm'
                  textDecoration='underline'
                  width='fit-content'
                  onClick={onToggle}
                >
                  {isClamped ? 'Read More' : 'Minimize'}
                </Text>
              );
            }}
          >
            <Text
              as='span'
              sx={{
                '&::before': {
                  content: '"\\201C"',
                },
                '&::after': {
                  content: '"\\201D"',
                },
              }}
            >
              {review.content?.trim()}
            </Text>
          </ClampedTextV2>
        </Box>
      </Flex>
      <Flex
        alignItems='center'
        bgColor='supportGray.10'
        gap={4}
        flex={{
          md: '0 1 238px',
        }}
        padding={{
          base: 2,
          md: 4,
        }}
      >
        <Avatar
          bgColor={avatarColor}
          getInitials={(name) => name?.[0]}
          name={review.consumer.name}
          src={undefined}
          display={{
            base: 'none',
            md: 'flex',
          }}
          sx={{
            '> .chakra-avatar__initials': {
              color: 'black',
            },
          }}
        />
        <Flex
          flex='1'
          justifyContent='space-between'
          alignItems={{
            base: 'center',
            md: 'flex-start',
          }}
          flexDirection={{
            base: 'row',
            md: 'column',
          }}
        >
          <Text as='b' color='supportGray.100' fontSize='sm' fontWeight='600' lineHeight='sm'>
            {review.consumer.name}
          </Text>
          <Text color='supportGray.60' fontSize='xs' lineHeight='xs'>
            {formattedDate}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
}

function ReviewSkeletonCard() {
  return (
    <Card
      as='article'
      border='1px solid'
      borderColor='supportBlue.20'
      borderRadius='8px'
      boxShadow='none'
      display='flex'
      overflow='hidden'
      flexDirection={{
        base: 'column',
        md: 'row',
      }}
    >
      <Flex
        flex='1'
        flexWrap='wrap'
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: 2,
          md: 4,
        }}
        padding={{
          base: 2,
          md: 6,
        }}
      >
        <Box
          flex={{
            md: '0 0 80px',
          }}
        >
          <Rating rating={5} isLoading />
        </Box>
        <Stack
          flex='1'
          minHeight={{
            md: '46.5px',
          }}
        >
          <Skeleton height='22px' width='100%' />
        </Stack>
      </Flex>
      <Flex
        alignItems='center'
        bgColor='supportGray.10'
        gap={4}
        flex={{
          md: '0 1 238px',
        }}
        padding={{
          base: 2,
          md: 4,
        }}
      >
        <Skeleton
          borderRadius='50%'
          height='48px'
          width='48px'
          display={{
            base: 'none',
            md: 'block',
          }}
        />
        <Stack
          flex='1'
          justifyContent='space-between'
          spacing={2}
          flexDirection={{
            base: 'row',
            md: 'column',
          }}
        >
          <Skeleton height='22px' width='57px' />
          <Skeleton height='16px' width='60px' />
        </Stack>
      </Flex>
    </Card>
  );
}

export function transformStaticReviewsSectionProps({
  programKey,
  reviews,
  reviewSummary,
}: {
  programKey: string;
  reviews: ProgramReview[];
  reviewSummary: ProgramReviewSummary;
}) {
  if (reviewSummary.numberOfReviews < REVIEW_MINIMUM_THRESHOLD) {
    return null;
  }

  return {
    programKey,
    initReviews: reviews.slice(0, 5),
    numberOfReviews: reviewSummary.numberOfReviews,
    starsAverage: reviewSummary.starsAverage,
  };
}

export type StaticReviewsSectionProps = ReturnType<typeof transformStaticReviewsSectionProps>;
